/**
 * CANONICAL MODULE — app-platform/shared/authAutoLogin.ts
 *
 * This file is copied byte-for-byte into every app (`src/lib/authAutoLogin.ts`). It imports nothing
 * from any app and touches no storage, no MSAL and no browser API: it is one pure function, so the
 * rule it encodes can be tested exhaustively.
 *
 * **Fix bugs HERE, then copy outward. Never edit an app's copy in place.**
 *
 * ── Why an automatic sign-in bounce exists at all ────────────────────────────────────────────────
 * This is a **structural MSAL v4+ constraint, not a preference, and it must not be "simplified"
 * away by a later reader.** MSAL v4+ encrypts its localStorage auth cache with a key held in a
 * session-scoped cookie, which dies when the browser closes. So a persisted account cannot be
 * restored on a cold reopen unless the user ticked "Keep me signed in" — a prompt that never appears
 * in the Edge + passkey flow, and one there is no way to force. Without a workaround, every cold
 * reopen is signed out.
 *
 * The workaround: on a signed-out cold start, start the login **redirect** (never a popup — COOP
 * severs `window.opener`, which broke this in a sibling app already), so the persistent Microsoft
 * session bounces straight back in with no click.
 *
 * ── Why all five guards, together ────────────────────────────────────────────────────────────────
 * An unguarded bounce is hostile: it throws a first-time visitor at a Microsoft login screen before
 * they have seen the app, it signs a user back in seconds after they deliberately signed out, it
 * loops forever when sign-in keeps failing, and it drags automated test runs onto live Microsoft
 * infrastructure. Each guard below exists because one of those actually happened somewhere in this
 * estate; no app had all five before W9.
 *
 * This function decides; the caller performs the redirect and records the attempt. Keeping the
 * effect out means every branch is testable without a browser.
 */

export interface AutoLoginInput {
  /** MSAL has finished initialising and handling any redirect response. */
  ready: boolean
  /** An account is already present — nothing to do. */
  hasAccount: boolean
  /** `navigator.onLine`. A bounce with no network strands the user on a dead Microsoft page. */
  online: boolean
  /** `navigator.webdriver`. Keeps automated runs off live Microsoft sign-in. */
  automated: boolean
  /** This device has completed a sign-in before. A brand-new visitor must see the app first. */
  returningVisitor: boolean
  /** The user chose to sign out. An automatic bounce must never overrule that. */
  deliberateSignOut: boolean
  /** We already bounced in this tab session. One per tab, so a failing sign-in cannot loop. */
  alreadyAttempted: boolean
}

/**
 * Should the app start an automatic sign-in redirect right now?
 *
 * All seven conditions must hold. Order is not significant to the result, but it is written
 * cheapest-and-most-common first so the common "already signed in" case exits immediately.
 *
 * Apps with button-only sign-in (trakr) simply never call this.
 */
export function shouldAutoLogin(input: AutoLoginInput): boolean {
  if (!input.ready) return false
  if (input.hasAccount) return false
  if (input.alreadyAttempted) return false
  if (!input.online) return false
  if (input.automated) return false
  if (input.deliberateSignOut) return false
  if (!input.returningVisitor) return false
  return true
}
